import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Layers, ShieldCheck } from 'lucide-react';
import Logo from './Logo';
import Button from './Button';

const NAV_LINKS = [
  { to: '/product',  label: 'Product',  icon: Layers      },
  { to: '/security', label: 'Security', icon: ShieldCheck },
];

const DesktopNav = ({ pathname }) => {
  const [hovered, setHovered] = useState(null);

  return (
    <div
      onMouseLeave={() => setHovered(null)}
      className="hidden md:flex items-center absolute left-1/2 -translate-x-1/2 p-1 rounded-full bg-white/[0.025] ring-1 ring-white/[0.06] backdrop-blur-md"
    >
      {NAV_LINKS.map(({ to, label, icon: Icon }) => {
        const isActive = !!matchPath({ path: to, end: false }, pathname);
        const isHovered = hovered === to;

        return (
          <NavLink
            key={to}
            to={to}
            onMouseEnter={() => setHovered(to)}
            className="relative px-4 py-2 group"
          >
            {/* hovered pill */}
            <AnimatePresence>
              {isHovered && (
                <motion.span
                  layoutId="nav-hover"
                  className="absolute inset-0 rounded-full bg-white/[0.07] ring-1 ring-white/[0.08]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 30, mass: 0.6 }}
                />
              )}
            </AnimatePresence>

            <span className="relative flex items-center gap-2">
              <Icon
                size={15}
                strokeWidth={1.8}
                className={`transition-colors duration-200 ${
                  isActive
                    ? 'text-amber'
                    : isHovered
                    ? 'text-canvas-white'
                    : 'text-canvas-white/65'
                }`}
              />
              <span
                className={`text-[14.5px] font-medium tracking-tight transition-colors duration-200 ${
                  isActive || isHovered
                    ? 'text-canvas-white'
                    : 'text-canvas-white/85'
                }`}
              >
                {label}
              </span>

              {/* active dot */}
              {isActive && (
                <motion.span
                  layoutId="nav-active-dot"
                  className="ml-0.5 w-1 h-1 rounded-full bg-amber shadow-[0_0_8px_rgba(204,132,0,0.7)]"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </span>
          </NavLink>
        );
      })}
    </div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? 'bg-canvas-black/75 backdrop-blur-xl border-b border-white/[0.06]'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <nav className="container-prose flex items-center justify-between h-20">
          <Logo size={34} />

          <DesktopNav pathname={location.pathname} />

          <div className="hidden md:flex items-center gap-2">
            <Button to="/request-demo" variant="primary" size="md" withArrow>
              Book a demo
            </Button>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-canvas-white p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="md:hidden border-t border-white/[0.06] bg-canvas-black/95 backdrop-blur-xl"
            >
              <div className="container-prose py-4 flex flex-col gap-1">
                {NAV_LINKS.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2.5 text-[15px] rounded-lg ${
                        isActive ? 'text-canvas-white bg-white/[0.06]' : 'text-ink-faint'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          size={15}
                          strokeWidth={1.8}
                          className={isActive ? 'text-amber' : 'text-ink-faint/70'}
                        />
                        {label}
                      </>
                    )}
                  </NavLink>
                ))}
                <Button to="/request-demo" variant="primary" size="md" withArrow className="mt-2 self-start">
                  Book a demo
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Navbar;
